/**
 * Specifiche precise per calcolo lot size per valuta/asset.
 * Stessa logica della mobile-app per coerenza web/app.
 *
 * Formule pip value (conto in USD, 1 lotto standard):
 * - XXX/USD (EURUSD, GBPUSD): 100.000 × 0.0001 = $10/pip
 * - USD/XXX (USDCHF, USDCAD): 100.000 × 0.0001 / rate = 10/rate
 * - XXX/JPY (USDJPY, EURJPY): 1000/rate
 * - Cross (AUDNZD, EURGBP, …): 10 × (quote in USD)
 * - XAUUSD: $1/pip | XAGUSD: $50/pip | Indici: $1/punto
 */
const CONTRACT_SIZE_FOREX = 100000;

export function getPipSize(symbol) {
  const s = (symbol || '').toUpperCase();
  if (s.includes('JPY')) return 0.01;
  if (s.includes('XAU') || s.includes('GOLD') || s.includes('XPT') || s.includes('XPD')) return 0.01;
  if (s.includes('XAG') || s.includes('SILVER')) return 0.01;
  if (/US30|US100|NAS100|US500|SPX|DAX|GER40|UK100|USTEC|SP500|NDX|FRA40|EU50/i.test(s)) return 1;
  return 0.0001;
}

export function getQuoteCurrency(symbol) {
  const str = (symbol || '').toUpperCase().replace(/\.+$/, '');
  if (str.length >= 6) return str.slice(3, 6);
  return null;
}

export function getPipValuePerLot(symbol, entryPrice, usdJpyRate, quoteToUsdRate) {
  const s = (symbol || '').toUpperCase().replace(/\.+$/, '');
  if (s.includes('JPY')) {
    if (!entryPrice || entryPrice <= 0) return 0;
    if (s === 'USDJPY') return (CONTRACT_SIZE_FOREX * 0.01) / entryPrice;
    const jpyPerUsd = usdJpyRate > 0 ? usdJpyRate : 150;
    return (CONTRACT_SIZE_FOREX * 0.01) / jpyPerUsd;
  }
  if (s.includes('XAU') || s.includes('GOLD') || s.includes('XPT') || s.includes('XPD')) return 1;
  if (s.includes('XAG') || s.includes('SILVER')) return 50;
  if (/US30|US100|NAS100|US500|SPX|DAX|GER40|UK100|USTEC|SP500|NDX|FRA40|EU50/i.test(s)) return 1;
  if (s.startsWith('USD')) {
    if (!entryPrice || entryPrice <= 0) return 0;
    return (CONTRACT_SIZE_FOREX * 0.0001) / entryPrice;
  }
  if (!s.endsWith('USD') && s.length === 6 && /^[A-Z]{6}$/.test(s)) {
    if (quoteToUsdRate != null && quoteToUsdRate > 0) return 10 * quoteToUsdRate;
    return 0;
  }
  return 10;
}

export function getPriceDecimals(symbol) {
  const s = (symbol || '').toUpperCase();
  if (s.includes('JPY')) return 2;
  if (s.includes('XAU') || s.includes('XAG') || s.includes('XPT') || s.includes('XPD') || s.includes('GOLD') || s.includes('SILVER')) return 2;
  if (/US30|US100|NAS100|US500|SPX|DAX|GER40|UK100|USTEC|SP500|NDX|FRA40|EU50/i.test(s)) return 1;
  return 5;
}

export function getPriceStep(symbol) {
  const pip = getPipSize(symbol);
  if (pip >= 1) return 1;
  if (pip >= 0.01) return 0.01;
  return 0.00001;
}

export function getLotDecimals() {
  return 2;
}

export const DEFAULT_ENTRY_BY_SYMBOL = {
  EURUSD: 1.085, GBPUSD: 1.265, USDJPY: 149.5, USDCHF: 0.88, AUDUSD: 0.655, USDCAD: 1.36, NZDUSD: 0.61,
  EURJPY: 162, GBPJPY: 189, EURGBP: 0.858, EURCHF: 0.95, EURAUD: 1.65, EURCAD: 1.47, EURNZD: 1.78,
  GBPCHF: 1.12, GBPAUD: 1.92, GBPCAD: 1.72, GBPNZD: 2.08, AUDJPY: 97.8, AUDNZD: 1.09, AUDCAD: 0.89, AUDCHF: 0.575,
  CADJPY: 109.5, CHFJPY: 169.5, NZDJPY: 91.2, NZDCAD: 0.82, NZDCHF: 0.535, CADCHF: 0.645, EURNOK: 11.5, EURSEK: 11.2,
  USDNOK: 10.6, USDSEK: 10.35, USDMXN: 17.2, USDTRY: 34.5, USDZAR: 18.5, USDPLN: 3.95, USDDKK: 6.85,
  XAUUSD: 2650, XAGUSD: 31.2, XPTUSD: 1050, XPDUSD: 980,
  US30: 45000, US100: 21500, NAS100: 21500, US500: 5850, SPX: 5850, DAX: 20500, GER40: 20500, UK100: 8400,
  FRA40: 8050, EU50: 4950, USTEC: 21500, SP500: 5850, NDX: 21500,
};

function getQuoteToUsdFallback(symbol) {
  const quote = getQuoteCurrency(symbol);
  if (!quote) return 0;
  const v = DEFAULT_ENTRY_BY_SYMBOL[quote + 'USD'];
  if (v != null && v > 0) return v;
  const w = DEFAULT_ENTRY_BY_SYMBOL['USD' + quote];
  if (w != null && w > 0) return 1 / w;
  return 0;
}

/** Da lista prezzi (array con symbol, bid, ask) ricava usdJpyRate e quoteToUsdRate per il simbolo. */
export function getRatesFromPrices(prices, symbol) {
  const list = Array.isArray(prices) ? prices : [];
  const norm = (s) => (s || '').replace(/\.+$/, '').toUpperCase();
  const mid = (p) => {
    if (!p || (p.bid == null && p.ask == null)) return 0;
    return typeof p.bid === 'number' && p.bid > 0 ? p.bid : (p.ask != null && p.bid != null ? (p.bid + p.ask) / 2 : p.ask ?? p.bid);
  };
  const sym = norm(symbol);
  let usdJpyRate = 0;
  const usdJpy = list.find((p) => norm(p.symbol) === 'USDJPY');
  if (usdJpy) usdJpyRate = mid(usdJpy);

  let quoteToUsdRate = 0;
  const quote = getQuoteCurrency(symbol);
  const isCross = quote && sym.length === 6 && !sym.endsWith('USD') && !sym.startsWith('USD') && !sym.includes('JPY');
  if (isCross && quote) {
    const quoteUsd = list.find((p) => norm(p.symbol) === quote + 'USD');
    const usdQuote = list.find((p) => norm(p.symbol) === 'USD' + quote);
    if (quoteUsd && mid(quoteUsd) > 0) quoteToUsdRate = mid(quoteUsd);
    else if (usdQuote && mid(usdQuote) > 0) quoteToUsdRate = 1 / mid(usdQuote);
  }
  if (isCross && quoteToUsdRate <= 0) quoteToUsdRate = getQuoteToUsdFallback(symbol);
  return { usdJpyRate, quoteToUsdRate };
}
